import React, { useEffect, useRef, useState } from 'react';
import NumericInput from './numeric-input';
import { useWheelStep } from './use-wheel-step';

interface NumberFieldProps {
  value: number | undefined;
  onChange: (value: number) => void;
  step?: number;
  min?: number;
  max?: number;
  placeholder?: string;
  /**
   * Value applied when the field is cleared and loses focus. When omitted, an
   * empty field reverts to its previous value on blur (non-destructive) — so a
   * stray clear never silently zeroes an unrelated prop (opacity, size, …).
   */
  emptyValue?: number;
}

const toDisplay = (value: number | undefined): string =>
  value === undefined || Number.isNaN(value) ? '' : String(Number(value.toFixed(2)));

const NumberField: React.FC<NumberFieldProps> = ({ value, onChange, step = 1, min, max, placeholder, emptyValue }) => {
  const external = toDisplay(value);
  // Local draft so the field can be temporarily empty mid-edit. A fully
  // controlled input rejected the empty string (parseFloat('') is NaN, so no
  // onChange fired and React forced the old value back) — deleting the only
  // digit appeared to do nothing.
  const [draft, setDraft] = useState(external);
  const inputRef = useRef<HTMLInputElement>(null);

  // Adopt the external value when it changes from outside the field, but leave
  // an in-progress draft alone when it already parses to the current value — so
  // a freshly-cleared field stays empty and a partial decimal like "0." survives.
  useEffect(() => {
    setDraft((d) => (parseFloat(d) === value ? d : external));
  }, [external, value]);

  const parsedDraft = parseFloat(draft);
  const currentNumeric = Number.isNaN(parsedDraft) ? value : parsedDraft;
  useWheelStep(inputRef, currentNumeric, {
    step,
    min,
    max,
    onStep: (next) => {
      setDraft(toDisplay(next));
      onChange(next);
    },
  });

  return (
    <NumericInput
      ref={inputRef}
      value={draft}
      step={step}
      min={min}
      max={max}
      placeholder={placeholder}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        const v = parseFloat(raw);
        if (!Number.isNaN(v)) onChange(v);
      }}
      onBlur={() => {
        if (draft === '') {
          if (emptyValue !== undefined && emptyValue !== value) onChange(emptyValue);
          setDraft(toDisplay(emptyValue ?? value));
        }
      }}
    />
  );
};

export default NumberField;
